import {StyleSheet} from 'aphrodite';
import _ from 'underscore';
import {v4 as uuidv4} from 'uuid';

import {
  BenchlingExpression,
  BenchlingExpressionConstantValue,
  BenchlingExpressionFunction,
  BenchlingExpressionFunctionParameter,
  BenchlingExpressionType,
} from 'benchling-expressions/BenchlingExpressionTypes';
import {
  BenchlingFunctionSignature,
  EqualsFunctionSignature,
  HasNoneOfFunctionSignature,
  HasOneOfFunctionSignature,
  NotEqualsFunctionSignature,
  NumericEqualsFunctionSignature,
  NumericNotEqualsFunctionSignature,
  GreaterThanFunctionSignature,
  GreaterThanOrEqualToFunctionSignature,
  LessThanFunctionSignature,
  LessThanOrEqualToFunctionSignature,
  ContainsStringFunctionSignature,
  NotContainsStringFunctionSignature,
  HasSchemaFunctionSignature,
  NotHasSchemaFunctionSignature,
  IsBlankFunctionSignature,
  IsPresentFunctionSignature,
  AndFunctionSignature,
  OrFunctionSignature,
} from 'benchling-expressions/BenchlingFunctionSignatures';
import {LeafType, isSubType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {ProcessFlowchartEdgeConfigSpec} from 'processes/dao/ProcessSchemaDAO';
import {FieldType, FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import {DeepPartial} from 'util/Types';

export const DEFAULT_FUNCTION_SPEC_DEFAULT_NAME = 'All other cases';

export const DELETE_ROUTER_CONFIRMATION_MODAL_TITLE = 'Delete router?';
export const DELETE_ROUTER_CONFIRMATION_MODAL_MESSAGE =
  'Deleting this router will remove it from this flowchart and any routes configured in the router.';

export enum ROUTER_ERROR_TYPES {
  INVALID_LOOKUP = 'invalid_lookup_type',
  DUPLICATED_NODE_ERROR_TYPE = 'multiple_nodes_routing_to_downstream_node',
  TOO_MANY_EXPRESSIONS = 'too_many_expressions',
  GENERIC_ERROR = 'generic_error',
}

export const UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_SPEC: BaseRouterFunctionSpec &
  ClientCreateRouterFunctionSpec = {
  name: DEFAULT_FUNCTION_SPEC_DEFAULT_NAME,
  clientId: '',
  isDefault: true,
  isConfigured: false,
};

export const UNCONFIGURED_NON_DEFAULT_ROUTER_FUNCTION_SPEC: BaseRouterFunctionSpec &
  ClientCreateRouterFunctionSpec = {
  name: '',
  clientId: '',
  isDefault: false,
  isConfigured: false,
};

export const UNCONFIGURED_DEFAULT_ROUTER_EXPRESSION: DeepPartial<BenchlingExpressionFunction> = {
  name: undefined,
  type: BenchlingExpressionType.FUNCTION,
  parameters: [],
};

export const UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_PARAMETER: DeepPartial<BenchlingExpressionFunctionParameter> =
  {
    name: AndFunctionSignature.parameters[0].spreadParameterName,
    expression: UNCONFIGURED_DEFAULT_ROUTER_EXPRESSION,
  };

export function getUnconfiguredRouterConfig(name: string): RouterConfig {
  return {
    name,
    clientId: uuidv4(),
    defaultFunctionSpec: {...UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_SPEC, clientId: uuidv4()},
    functionSpecs: [],
  };
}

interface BaseRouterFunctionSpec {
  clientId: string;
  edgeConfigReference?: ProcessFlowchartEdgeConfigSpec;
  name: string;
  isDefault: boolean;
}

interface ClientCreateRouterFunctionSpec {
  expression?: DeepPartial<BenchlingExpression> | null;
  isConfigured: false;
}

export type CreateRouterFunctionSpec = BaseRouterFunctionSpec & ClientCreateRouterFunctionSpec;

interface ClientUpdateRouterFunctionSpec {
  expression?: BenchlingExpression | null;
  isConfigured: true;
}

export type UpdateRouterFunctionSpec = BaseRouterFunctionSpec & ClientUpdateRouterFunctionSpec;

export type CreateOrUpdateRouterFunctionSpec = CreateRouterFunctionSpec | UpdateRouterFunctionSpec;

export interface RouterConfig {
  clientId: string;
  name: string;
  defaultFunctionSpec: CreateOrUpdateRouterFunctionSpec | null;
  functionSpecs: Array<CreateOrUpdateRouterFunctionSpec>;
}

export const SUPPORTED_LOOKUP_SCHEMA_FIELD_TYPES: Array<FieldType> = [
  FieldType.SELECTOR,
  FieldType.INTEGER,
  FieldType.FLOAT,
  FieldType.TEXT,
  FieldType.BIOENTITY_LINK,
  FieldType.STORABLE_LINK,
  FieldType.ENTRY_LINK,
];

export enum LookupReturnType {
  SINGLE_VALUE,
  MULTIPLE_VALUES,
}

export const ALLOWED_ROUTING_OPERATORS_BY_FUNCNAME = _.indexBy(
  [
    EqualsFunctionSignature,
    NotEqualsFunctionSignature,
    HasOneOfFunctionSignature,
    HasNoneOfFunctionSignature,
    NumericEqualsFunctionSignature,
    NumericNotEqualsFunctionSignature,
    GreaterThanFunctionSignature,
    GreaterThanOrEqualToFunctionSignature,
    LessThanFunctionSignature,
    LessThanOrEqualToFunctionSignature,
    ContainsStringFunctionSignature,
    NotContainsStringFunctionSignature,
    HasSchemaFunctionSignature,
    NotHasSchemaFunctionSignature,
    IsBlankFunctionSignature,
    IsPresentFunctionSignature,
  ],
  'functionName'
);

type MultiplexityToOperatorHash = {
  [key in LookupReturnType]?: Array<BenchlingFunctionSignature>;
};

const numberOperators: MultiplexityToOperatorHash = {
  [LookupReturnType.SINGLE_VALUE]: [
    NumericEqualsFunctionSignature,
    NumericNotEqualsFunctionSignature,
    GreaterThanFunctionSignature,
    GreaterThanOrEqualToFunctionSignature,
    LessThanFunctionSignature,
    LessThanOrEqualToFunctionSignature,
    IsBlankFunctionSignature,
    IsPresentFunctionSignature,
  ],
};

const schemaOperators: MultiplexityToOperatorHash = {
  [LookupReturnType.SINGLE_VALUE]: [
    HasSchemaFunctionSignature,
    NotHasSchemaFunctionSignature,
    IsBlankFunctionSignature,
    IsPresentFunctionSignature,
  ],
};

export const BOOLEAN_OPERATORS = [AndFunctionSignature, OrFunctionSignature];

export const BOOLEAN_OPERATORS_BY_FUNCNAME = _.indexBy(
  [AndFunctionSignature, OrFunctionSignature],
  'functionName'
);

function getAllowedRoutingOperatorsByValueType(): {[key in LeafType]?: MultiplexityToOperatorHash} {
  const operators: {[key in LeafType]?: MultiplexityToOperatorHash} = {
    [LeafType.DROPDOWN_OPTION]: {
      [LookupReturnType.SINGLE_VALUE]: [
        EqualsFunctionSignature,
        NotEqualsFunctionSignature,
        IsBlankFunctionSignature,
        IsPresentFunctionSignature,
      ],
      [LookupReturnType.MULTIPLE_VALUES]: [
        HasOneOfFunctionSignature,
        HasNoneOfFunctionSignature,
        IsBlankFunctionSignature,
        IsPresentFunctionSignature,
      ],
    },
  };

  operators[LeafType.INTEGER] = numberOperators;
  operators[LeafType.DECIMAL] = numberOperators;
  operators[LeafType.TEXT] = {
    [LookupReturnType.SINGLE_VALUE]: [
      EqualsFunctionSignature,
      NotEqualsFunctionSignature,
      ContainsStringFunctionSignature,
      NotContainsStringFunctionSignature,
      IsBlankFunctionSignature,
      IsPresentFunctionSignature,
    ],
  };

  operators[LeafType.BIOENTITY] = schemaOperators;
  operators[LeafType.STORABLE] = schemaOperators;
  operators[LeafType.LOCATION] = schemaOperators;
  operators[LeafType.ENTRY] = schemaOperators;
  operators[LeafType.RESULT] = schemaOperators;
  operators[LeafType.REQUEST] = schemaOperators;
  operators[LeafType.RUN] = schemaOperators;
  return operators;
}

export function maybeCastToParentRouterLeafType(typeDef: LeafType): LeafType {
  if (isSubType(typeDef, LeafType.BIOENTITY)) {
    return LeafType.BIOENTITY;
  } else if (isSubType(typeDef, LeafType.STORABLE)) {
    return LeafType.STORABLE;
  } else {
    return typeDef;
  }
}

export function getAllowedRoutingOperatorsForValueType(
  typeDef: LeafType
): MultiplexityToOperatorHash | undefined {
  return getAllowedRoutingOperatorsByValueType()[maybeCastToParentRouterLeafType(typeDef)];
}

export const isSupportedRouterLookupType = (typeDef: LeafType): boolean =>
  getAllowedRoutingOperatorsForValueType(typeDef) !== undefined;

// This is only for display.
export const getFriendlySupportedRouterFieldTypes = (): Array<string> => {
  const entries = Object.entries(FIELD_TYPES).filter(([_name, fullFieldType]) =>
    SUPPORTED_LOOKUP_SCHEMA_FIELD_TYPES.includes(fullFieldType.type)
  );

  return entries.map(([_name, fullFieldType]) => fullFieldType.label);
};

export const DEFAULT_FUNCTION_NAME = 'Route 1';
export const DEFAULT_DEFAULT_FUNCTION_NAME = 'All other cases';

export const ROUTER_CONFIGURATION_MODAL_TYPE = 'router_node_configuration';

export const ROUTER_CONTAINER_NAME = 'router-config-form-container';

export const ROUTER_CSS_CONTAINER_QUERY = `@container ${ROUTER_CONTAINER_NAME} (min-width: 680px)`;

export const sharedStyles = StyleSheet.create({
  routerOrRouteName: {
    ...FONT_PROXIMA_TITLE,
    overflowX: 'hidden',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingBlock: 4,
  },
  routerConfigFormContainer: {
    containerName: ROUTER_CONTAINER_NAME,
    containerType: 'inline-size',
  },
  routerInputControl: {
    width: 140,
    [ROUTER_CSS_CONTAINER_QUERY]: {
      width: 190,
    },
  },
});

export interface ConditionalLogicAllowedValuesBaseProps {
  expression: DeepPartial<BenchlingExpressionFunction> | null;
  setExpression: (newExpression: DeepPartial<BenchlingExpressionFunction>) => void;
  constantExpression: DeepPartial<BenchlingExpressionConstantValue> | null;
}
